import { useDispatch } from "react-redux";
import type { AppDispatch } from "../redux/store/store";
import { logout, showOrganizationPicker } from "../redux/slice/layoutSlice";
import { logout as clearAuthSession } from "../redux/slice/authSlice";
import { useLogoutMutation, useMyOrganizationsQuery } from "../redux/api/platform/tokenApi";
import { IconClock } from "../components/icons";
import { AccountDeletionPanel } from "../components/AccountDeletionPanel";

interface PendingApprovalProps {
  /** Re-check the subscription status. */
  onRefresh: () => void;
  refreshing?: boolean;
}

// Shown to an authenticated org user whose subscription is not yet active.
// They get access to the modules once the platform owner activates it.
export const PendingApproval = ({ onRefresh, refreshing }: PendingApprovalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [logoutMutation] = useLogoutMutation();

  // Offer to switch only when the user actually belongs to more than one org.
  const { data: orgs = [] } = useMyOrganizationsQuery();
  const canSwitch = orgs.length > 1;

  const handleLogout = () => {
    const sessionToken = localStorage.getItem("sessionToken");
    if (sessionToken) {
      logoutMutation(sessionToken)
        .unwrap()
        .catch(() => undefined);
    }
    dispatch(clearAuthSession());
    dispatch(logout());
  };

  return (
    <div className="login">
      <div className="login__card">
        <span className="login__logo">
          <IconClock size={26} />
        </span>
        <h1 className="login__title">Account pending approval</h1>
        <p className="login__sub">
          Thanks for signing up! Your subscription is awaiting activation by the
          platform team. You'll get access to your workspace as soon as it's
          approved.
        </p>

        <button
          type="button"
          className="btn btn--primary login__submit"
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing ? "Checking…" : "Check status"}
        </button>

        <p className="login__hint">
          This usually takes a little while. You can check back anytime.
        </p>

        {canSwitch && (
          <button
            type="button"
            className="btn btn--ghost login__back"
            onClick={() => dispatch(showOrganizationPicker())}
          >
            Switch Organization?
          </button>
        )}

        <button
          type="button"
          className="btn btn--ghost login__back"
          onClick={handleLogout}
        >
          Log out
        </button>

        {/* A user whose subscription is never activated can't reach Profile,
            so without this they'd have no in-app way to delete their account
            at all — which both app stores require. */}
        <hr className="login__divider" />
        <AccountDeletionPanel variant="bare" />
      </div>
    </div>
  );
};

export default PendingApproval;
